import type { ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

/** Unwraps the `{success, message, data}` envelope from any axios call. */
export const unwrap = <T,>(p: Promise<{ data: { data: T } }>): Promise<T> => p.then((r) => r.data.data);

export interface Meta { page: number; per_page: number; total: number; last_page: number }
export interface Paged<T> { data: T[]; meta: Meta }
export const paged = <T,>(p: Promise<{ data: Paged<T> }>): Promise<Paged<T>> => p.then((r) => r.data);

export function errMsg(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const d = (e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response?.data;
  const first = d?.errors ? Object.values(d.errors)[0]?.[0] : undefined;
  return first ?? d?.message ?? fallback;
}

export const field = 'border rounded-xl w-full px-3 py-2 text-sm';

export const Loading = ({ text = 'Loading…' }: { text?: string }) => <p className="text-sm text-slate-500">{text}</p>;
export const Empty = ({ text }: { text: string }) => <p className="text-sm text-slate-500">{text}</p>;

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="flex items-center gap-3">
      <p className="text-sm text-red-600">{errMsg(error)}</p>
      {onRetry && <Button variant="ghost" onClick={onRetry}>Retry</Button>}
    </div>
  );
}

export function Banner({ tone = 'success', children, onClose }: { tone?: 'success' | 'error'; children: ReactNode; onClose?: () => void }) {
  return (
    <p className={`text-sm rounded-xl px-3 py-2 flex justify-between gap-3 ${tone === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
      <span>{children}</span>
      {onClose && <button onClick={onClose} aria-label="Dismiss">✕</button>}
    </p>
  );
}

export function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-end sm:place-items-center z-40" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-card p-5 sm:p-6 w-full sm:w-[28rem] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Pager({ meta, onPage }: { meta?: Meta; onPage: (p: number) => void }) {
  if (!meta || meta.last_page <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 mt-3 text-sm">
      <Button variant="ghost" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)}>Previous</Button>
      <span className="text-slate-500">Page {meta.page} of {meta.last_page}</span>
      <Button variant="ghost" disabled={meta.page >= meta.last_page} onClick={() => onPage(meta.page + 1)}>Next</Button>
    </div>
  );
}

export function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-4 min-w-0">
      <div className="text-xs sm:text-sm text-slate-500 truncate">{label}</div>
      <div className="text-lg sm:text-2xl font-extrabold mt-1 text-slate-900 truncate">{value}</div>
    </div>
  );
}

/** Page title block used across the parent portal. */
export function PageTitle({ kicker, title, sub, action }: { kicker?: string; title: string; sub?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
      <div className="min-w-0">
        {kicker && <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{kicker}</p>}
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{title}</h1>
        {sub && <p className="text-sm text-slate-500 mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

/** Lets a wide table scroll sideways on a phone instead of squashing. */
export const Scroll = ({ children }: { children: ReactNode }) => <div className="overflow-x-auto -mx-1 px-1">{children}</div>;

export const fmtDateTime = (d: string) => new Date(d).toLocaleString('en-NG', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export interface ChildCard {
  student_id: number; name: string; admission_no: string; photo_path: string | null; status: string; class: string | null;
  relationship: string | null; can_pay: boolean; is_primary: boolean; owing: number; results_held: boolean;
  attendance_month: { days: number; absent: number; rate: number | null } | null;
  today?: 'present' | 'absent' | 'late' | 'excused' | null;
}
